import React from 'react';
import { motion } from 'framer-motion';
import { Award, BookOpen, Heart, ShieldCheck } from 'lucide-react';

export default function About({ scrollToSection }) {
  const credentials = [
    {
      icon: <Award size={20} className="text-[#4A4A4A]" />,
      title: "Psykolog med praksisuddannelse",
      desc: "Uddannet Cand.psych. og psykolog med praksisuddannelse (tidl. autoriseret af Psykolognævnet under Socialministeriet)."
    },
    {
      icon: <ShieldCheck size={20} className="text-[#4A4A4A]" />,
      title: "Medlem af Dansk Psykologforening",
      desc: "Følger de etiske principper for nordiske psykologer og har fuld tavshedspligt."
    }
  ];

  return (
    <section id="om-mig" className="py-16 md:py-20 bg-[#F7F4F1] relative overflow-hidden">
      {/* Decorative background circle */}
      <div className="absolute top-[20%] left-[-10%] w-[45vw] h-[45vw] rounded-full bg-[#EDE6DF] blur-[100px] opacity-50 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Profile Image Column */}
          <div className="lg:col-span-5 order-2 lg:order-1 flex justify-center lg:justify-start">
            <div className="relative w-full max-w-[460px]">
              
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative aspect-[3/4] rounded-[32px] overflow-hidden shadow-xl"
              >
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/4291744f-0a58-4691-8a7f-39ecbae78b95/d2e35a5d-679c-49f9-b0cc-e4fb9488103f.png"
                  alt="Mette Brisson - Psykolog med praksisuddannelse"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#4A4A4A]/20 to-transparent pointer-events-none" />
              </motion.div>

              {/* Small floating badge */}
              <div className="absolute -bottom-6 -right-4 bg-white py-3 px-5 rounded-xl shadow-md border border-[#EDE6DF] flex items-center">
                <span className="font-sans-clean text-[11px] tracking-wider uppercase text-[#4A4A4A] font-semibold">
                  Mette Brisson
                </span>
              </div>
            </div>
          </div>

          {/* Text Content Column */}
          <div className="lg:col-span-7 order-1 lg:order-2 text-left">
            <h2 className="font-serif-elegant text-4xl md:text-5xl text-[#4A4A4A] font-light mb-6">
              Om mig
            </h2>
            <div className="w-12 h-[1px] bg-[#BFC8BE] mb-8" />

            <div className="font-sans-clean text-base text-[#4A4A4A]/80 space-y-6 mb-10 leading-relaxed">
              <p>
                Mit navn er Mette. Jeg er psykolog med praksisuddannelse med klinik i rolige rammer på Frederiksberg med nem adgang med offentlig transport. Jeg er uddannet i 2019 fra Københavns Universitet og har erfaring som PPR psykolog, hvor jeg særligt har arbejdet med børn, unge, deres forældre og givet sparring og supervision til de professionelle voksne omkring børnene. Jeg arbejder til daglig i dag i kommunalt regi med behandling af unge med angst og udarbejder psykologiske undersøgelser af børn og deres forældre. I 2024 valgte jeg at starte min egen praksis, og sideløbende med dette tage en terapeutisk efteruddannelse inden for NARM. Jeg har under hele mit virke som psykolog været særligt optaget af hvordan vi er formet af vores relationer gennem livet med fokus på tilknytning. Mit ønske er at når du i mødet med mig kan genfinde din balance og skabe meningsfulde forandringer i din hverdag.
              </p>
              <p>
                I mit terapeutiske arbejde lægger jeg afgørende vægt på at skabe et trygt, varmt og fordomsfrit rum. Jeg tror på, at en stærk og tillidsfuld relation mellem klient og psykolog er fundamentet for enhver vellykket udvikling. Hos mig bliver du mødt præcis som den, du er, og med det, du bringer med dig.
              </p>
              <p>
                Min tilgang er helhedsorienteret og med udgangspunkt i forskellige metoder. Jeg tilpasser altid forløbet til dine specifikke behov.
              </p>
            </div>

            {/* Credentials List */}
            <div className="space-y-6 border-t border-[#EDE6DF] pt-8">
              {credentials.map((cred, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="flex gap-4"
                >
                  <div className="flex-shrink-0 pt-1 text-[#4A4A4A]">
                    {cred.icon}
                  </div>
                  <div>
                    <h4 className="font-serif-elegant text-lg text-[#4A4A4A] font-medium mb-1">
                      {cred.title}
                    </h4>
                    <p className="font-sans-clean text-xs text-[#4A4A4A]/60 leading-relaxed">
                      {cred.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-10">
              <button
                onClick={() => scrollToSection('kontakt')}
                className="px-8 py-4 bg-[#BFC8BE] hover:bg-[#BFC8BE]/90 text-white font-sans-clean text-xs tracking-widest uppercase rounded-sm transition-all duration-300 shadow-sm hover:shadow-md"
              >
                Kontakt mig for en samtale
              </button>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
}
